import { cn } from '@module/lib/utils';
import type { HTMLProps } from 'react';

interface Props extends HTMLProps<HTMLButtonElement> {
  label: string;
  labelPrefix?: React.ReactNode;
  variant?: 'default' | 'radio' | 'simple';
  labelClassName?: string;
  onLabelClick?: () => void;
  suffix?: React.ReactNode;
  onSuffixClick?: () => void;
  onClick: () => void;
}

function BaseCheckbox({
  label,
  labelPrefix,
  variant = 'default',
  onClick,
  checked,
  className,
  labelClassName,
  onLabelClick,
  suffix,
  onSuffixClick,
  ...props
}: Props) {
  return (
    <div className={cn('flex w-fit items-center', className)} data-checkbox-state={checked}>
      <button id={label} onClick={onClick} type='button' disabled={props.disabled}>
        {checked && (
          <div
            className={cn('relative h-[24px] w-[24px] rounded-full border-2 border-primaryFg bg-primaryFg', {
              'h-[20px] w-[20px] border-none bg-transparent': variant === 'simple',
            })}
          >
            <div
              className={cn('absolute', {
                '-rotate-45 -skew-x-12 top-[5px] left-[4px] h-[7px] w-[12px] transform border-white border-b-[2px] border-l-[2px]':
                  variant === 'default',
                '-rotate-45 -skew-x-12 top-1 left-[4px] h-[9px] w-[16px] transform border-baseFg border-b-[3px] border-l-[3px]':
                  variant === 'simple',
                'top-[50%] left-[50%] h-[4px] w-[4px] translate-x-[-50%] translate-y-[-50%] rounded-full bg-white':
                  variant === 'radio',
              })}
            />
          </div>
        )}
        {!checked && (
          <div
            className={cn('relative h-[24px] w-[24px] rounded-full border-2 border-colorBg_cek4', {
              'h-[20px] w-[20px] border-none': variant === 'simple',
            })}
          >
            <div
              className={cn('absolute', {
                '-rotate-45 -skew-x-12 top-[5px] left-[4px] h-[7px] w-[12px] transform border-colorBg_cek4 border-b-[2px] border-l-[2px]':
                  variant === 'default',
                '-rotate-45 -skew-x-12 top-1 left-[4px] h-[9px] w-[16px] transform border-colorBg_cek4 border-b-[3px] border-l-[3px]':
                  variant === 'simple',
                'top-[50%] left-[50%] h-[4px] w-[4px] translate-x-[-50%] translate-y-[-50%] rounded-full bg-colorBg_cek4':
                  variant === 'radio',
              })}
            />
          </div>
        )}
      </button>
      <label
        className={cn(
          'flex break-all pl-1 text-left text-[17px] text-colorFg01',
          {
            'cursor-pointer': onLabelClick || onClick,
          },
          labelClassName,
        )}
        onClick={onLabelClick || onClick}
      >
        {labelPrefix}
        &nbsp;
        {label}
      </label>
      {suffix && (
        <div
          onClick={onSuffixClick}
          className={cn('flex grow justify-end', {
            'cursor-pointer': !!onSuffixClick,
          })}
        >
          {suffix}
        </div>
      )}
    </div>
  );
}

export default BaseCheckbox;
